<template>
	<div class="member-orders">
		<template v-if="showLinkedOrder">
			<h3>Länkad order</h3>
			<h4 v-if="linkedOrderError">Fel vid laddning av order</h4>
			<order v-else-if="linkedOrder" :order="linkedOrder" @cancel="cancel(linkedOrder)" />
			<loading-spinner v-else />
		</template>
		<h2 class="level-one-heading mb-5">Mina beställningar</h2>
		<hr>
		<div class="mb-5">
			Här hittar du samtliga beställningar och serviceärenden sedan användaren skapades. Saknar du äldre beställningar? Skicka då ordernummer på dessa till vår kundtjänst så lägger vi in dem på din användare!
		</div>
		<input-text
			v-if="filter !== 'service'"
			v-model="searchInput"
			class="mb-4"
			glyphicon="search"
			:placeholder="tm('ORDERS.SEARCH')"
			no-validation
			hide-valid
		/>

		<div class="list-settings">
			<toggle-button
				v-model="filter"
				activeValue="active"
				text="Aktiva"
				:counter="counts.active"
				sticky
			/>
			<toggle-button
				v-model="filter"
				activeValue="history"
				text="Hämtade/Skickade"
				:counter="counts.history"
				sticky
			/>
			<toggle-button
				v-model="filter"
				activeValue="cancelled"
				text="Avbeställda"
				:counter="counts.cancelled"
				sticky
			/>
			<toggle-button
				v-model="filter"
				activeValue="service"
				text="Service"
				:counter="counts.service"
				sticky
			/>
			<dropdown-select
				v-model="selectedSortingMethod"
				:items="availableSortingMethods"
				:formatter="v=>v"
			/>
		</div>
		<loading-spinner v-if="loading" />
		<template v-if="orders.length && filter !== 'service'">
			<paginator-buttons
				v-if="currentResultPageCount > 1"
				v-model="page"
				class="my-4"
				:nrPages="currentResultPageCount"
				:nrSelectablePages="nrSelectablePages"
			/>
			<order
				v-for="order in orders"
				class="my-4"
				:key="order.id"
				:order="order"
				showStatuses
				@cancel="extra => cancel(order, extra)"
			/>
			<paginator-buttons
				v-if="currentResultPageCount > 1"
				v-model="page"
				class="my-4"
				:nrPages="currentResultPageCount"
				:nrSelectablePages="nrSelectablePages"
			/>
		</template>
		<template v-if="serviceCases.length && filter === 'service'">
			<service-case
				v-for="serviceCase in serviceCases"
				:key="serviceCase.id"
				:serviceCase="serviceCase"
			/>
		</template>
		<center v-if="! loading" style="padding: 16px">
			<span v-if="filter === 'service' && ! serviceCases.length">
				Inga serviceärenden hittades
			</span>
			<span v-else-if="filter !== 'service' && ! orders.length">
				Inga beställningar hittades
			</span>
		</center>
	</div>
</template>

<script>
import mq from 'mediaQuery';
import waja from 'waja';
import store from 'datastore';
import { mapState } from 'vuex';
import { t } from 'datastore/i18n';
import orderUtils from 'utils/order';
import { debounce, throttle } from 'lodash';

export default {
	store,
	props: ['user'],
	data () {
		return {
			mq,
			orders: [],
			serviceCases: [],
			counts: {},
			loading: true,
			filter: 'active',
			linkedOrder: null,
			showingOrders: 4,
			searchInput: '',
			page: 1,
			currentResultPageCount: 0,
			selectedSortingMethod: null,
		};
	},
	mounted () {
		this.selectedSortingMethod = this.availableSortingMethods[0];
		this.loadOrders();
		if (this.$route.params.oid) {
			waja.get('order/' + this.$route.params.oid)
				.on('success', res => {
					this.linkedOrder = res.data.order;
				})
				.on('error', res => {
					this.linkedOrder = false;
				})
				.catch(res => {
					this.linkedOrder = false;
				})
				.go();
		}
	},
	methods: {
		showMoreOrders () {
			this.showingOrders += 4;
			if (this.showingOrders > this.orders.length) {
				this.showingOrders = this.orders.length;
			}
		},
		tm: t.prefix('MEMBER'),
		cancel (order, extraData) {
			orderUtils.cancel(order, extraData, () => {
				const index = this.orders.indexOf(order);
				if (index >= 0) {
					this.counts[this.filter]--;
					this.counts['cancelled']++;
					this.orders.splice(index, 1);
				}
			});
		},
		loadOrders: debounce(throttle(function () {
			const filters = {};
			filters[this.filter] = true;
			this.loading = true;

			waja.abort('order-list');
			waja.get('order/user/' + this.$route.params.id)
				.name('order-list')
				.data({
				  filters,
				  page: this.page,
				  searchString: this.searchInput,
				  sort: this.selectedSortingMethod,
				})
				.on('success', res => {
					this.loading = false;
					this.orders = res.data.orders;
					this.counts = res.data.counts;
					this.currentResultPageCount = res.data.currentResultPageCount;
				})
				.on('error', () => {
					this.$router.push(this.$router.route({
						name: 'member.achievements',
						params: {
							id: this.$route.params.id,
							name: this.$route.params.name,
						},
					}));
				}).go();
		}, 300), 0),
		loadServiceCases () {
			this.loading = true;
			this.orders = [];
			waja.get('rma')
				.on('success', res => {
					this.loading = false;
					this.serviceCases = res.data.cases;
				}).go();
		},
	},
	computed: {
		nrSelectablePages () {
			if ( this.mq.current < this.mq.sm) {
				return 4;
			}
			return this.mq.current < this.mq.md ? 5 : 7;
		},
		availableSortingMethods () {
			const available = [];
			if (this.filter === 'active') {
				available.push({
					label: 'Orderstatus',
					value: 'orderStatus',
				});
			} else if (this.filter === 'history') {
				available.push({
					label: 'Datum - hämtad/skickad',
					value: 'sentDate',
				});
			}
			available.push({
				label: 'Datum - beställning',
				value: 'orderDate',
			});
			return available;
		},
		serviceLink () {
			return 'https://classic.webhallen.com/medlem/' + this.currentUser.username + '/bestallningar/service';
		},
		linkedOrderError () {
			return this.$route.params.oid && this.linkedOrder === false;
		},
		showLinkedOrder () {
			return !! this.$route.params.oid;
		},
		...mapState({
			currentUser: state => state.user,
		}),
	},
	watch: {
		availableSortingMethods () {
			this.selectedSortingMethod = this.availableSortingMethods[0];
		},
		searchInput () {
			this.page = 1;
			this.loadOrders();
		},
		filter (val) {
			this.page = 1;
			if (val === 'service') {
				this.loadServiceCases();
			} else {
				this.loadOrders();
			}
		},
		page () {
			if (this.filter === 'service') {
				this.loadServiceCases();
			} else {
				this.loadOrders();
			}
		},
		selectedSortingMethod () {
			this.page = 1;
			this.loadOrders();
		},
	},
};
</script>
